import { useCallback } from 'react';
import { getirApi, migrosApi, yemeksepetiApi } from '../../../../config/apiClient';
import { TrendyolOrder } from '../types';
import {
  PackageKitchenReceiptData,
  PackageKitchenOrderItem,
  PackageKitchenOrderSubItem,
} from '../components/PackageKitchenPrintService';
import { PackageReceiptData, PackageReceiptItem, printPackageReceipt } from '../components/PackageReceiptPrintService';
import { getPackageOrderById } from '../../../../services/salesService';
import { convertTrendyolOrderToPackageOrder } from '../utils/helpers';
import { buildYemeksepetiReceiptItems, parseYsRawJsonData, buildGoogleMapsUrl } from '../utils/yemeksepetiReceiptBuilder';
import { getYsDeliveryLabel, isYsPickupOrder, formatYsPickupTime } from '../utils/yemeksepetiDelivery';
import { buildGetirReceiptItems } from '../utils/getirReceiptBuilder';
import {
  buildMigrosReceiptItems,
  buildMigrosCustomerAddress,
  buildMigrosOrderNote,
  calculateMigrosTotals,
} from '../utils/migrosReceiptBuilder';
import { translatePackagePaymentMethod } from '../utils/paymentMethodTranslator';
import { resolveReceiptHeader, resolveReceiptLabel, buildReceiptOrderRef } from '../utils/receiptHeader';
import QRCode from 'qrcode';

// ─── Tarih helper'lari ────────────────────────────────────────────────────
// Manuel YemeksepetiOrderDetailModal.tsx:277-285 ile birebir ayni mantik.
// Backend `createdAt` `Z` suffix'siz gelebiliyor; UTC olarak yorumlayip
// Istanbul TZ'de formatliyoruz ki fiste GMT+3 dogru basilsin.
// (Modal'a dokunmamak icin helper hook tarafinda yeniden tanimli; iki yer
// de literal ayni kalacak sekilde tutuluyor.)
const ISTANBUL_TZ = 'Europe/Istanbul';
const parseAsUtcDate = (dateStr?: string | null): Date | null => {
  if (!dateStr) return null;
  const hasTimezone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(dateStr);
  const normalized = hasTimezone ? dateStr : `${dateStr}Z`;
  const d = new Date(normalized);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * Yazdirilacak siparisi id + kaynak ile bulur.
 *
 * Kaynak verilmisse once onunla eslesen kayit aranir. Eslesme yoksa id-only
 * fallback yalnizca tek aday varsa kullanilir: iki platformun siparisi ayni
 * id'yi tasiyabildigi icin, birden fazla aday varken tahmin yurutmek yanlis
 * siparisin fisini bastirir.
 */
const findPrintTargetOrder = <T extends { id: number; source?: string }>(
  orders: T[],
  orderId: number,
  source?: string,
): T | undefined => {
  if (source) {
    const bySource = orders.find(o => o.id === orderId && o.source === source);
    if (bySource) return bySource;
  }

  const candidates = orders.filter(o => o.id === orderId);
  if (candidates.length === 1) return candidates[0];
  if (candidates.length > 1) {
    console.error('🖨️ [Print] Ayni id birden fazla kaynakta bulundu, siparis secilmedi:', {
      orderId, beklenenKaynak: source, adaylar: candidates.map(c => c.source),
    });
  }
  return undefined;
};

export interface UseKitchenPrintHandlersDeps {
  companyId: string | null;
  screenData: any;
  filteredOrders: any[];
  trendyolOrders: TrendyolOrder[];
  qrMenuOrders: any[];
  ecommerceOrders: any[];
  kioskOrders: any[];
  printPackageKitchenReceipt: (data: PackageKitchenReceiptData) => Promise<void>;
  printPackageKitchenReceiptByCategory: (data: PackageKitchenReceiptData) => Promise<void>;
  refreshGetirToken: () => void;
  trendyolProductDescriptions?: Map<number, string>;
  platformCategoryMappingLookup?: Map<string, { categoryId: string; categoryName: string }>;
  /**
   * Yazdırma başarısızlıklarını üst akışa bildirir. `true` dönerse hata bir
   * toplayıcı tarafından sahiplenildi (örn. oto kabul akışı, sonunda hata
   * modalı gösterecek) ve `alert()` GÖSTERİLMEZ. `false`/tanımsız ise eski
   * davranış korunur: kullanıcı `alert()` ile uyarılır.
   *
   * Sebep: bu handler'lar hatayı `alert` + `return` ile yutuyordu; oto kabul
   * akışının `try/catch`i hiç tetiklenmediği için başarısız onay/yazdırma
   * sessizce kaybolabiliyordu.
   */
  reportFailure?: (message: string) => boolean;
}

export function useKitchenPrintHandlers(deps: UseKitchenPrintHandlersDeps) {
  const {
    companyId,
    screenData,
    filteredOrders,
    trendyolOrders,
    qrMenuOrders,
    ecommerceOrders,
    kioskOrders,
    printPackageKitchenReceipt,
    printPackageKitchenReceiptByCategory,
    refreshGetirToken,
    trendyolProductDescriptions,
    platformCategoryMappingLookup,
    reportFailure,
  } = deps;

  /**
   * Yazdırma hatası bildirimi. Aktif bir toplayıcı varsa ona teslim edilir,
   * yoksa eski `alert()` davranışına düşer. Eskiden sadece `console.warn`
   * yazılan sessiz dallar da artık buradan geçiyor.
   */
  const failPrint = useCallback((message: string) => {
    console.error('🖨️ [Print] Başarısız:', message);
    if (reportFailure?.(message)) return;
    alert(message);
  }, [reportFailure]);

  /**
   * Paket sipariş için mutfak fişi yazdırır.
   *
   * @param orderId Sipariş ID
   * @param source Sipariş kaynağı ('phone' | 'getir' | 'yemeksepeti' | 'trendyol' | 'migros' | 'qr' | 'kiosk' | 'ecommerce')
   * @param options.itemIdsToPrint **Delta print** — verilirse SADECE bu ID'lere
   *   sahip kalemler mutfak fişine basılır. Telefon ilave sipariş akışında
   *   `addItemsToPackageOrder`'dan dönen `AddedItemIds` buraya iletilir; eski
   *   kalemler yeniden basılmaz. Diğer kaynaklarda (Trendyol/Getir/YS/Migros vb.)
   *   bu opsiyon ŞU AN için kullanılmıyor (kaynak-spesifik akışlar farklı).
   * @param options.isAdditionalOrder Fişin başlığını "⚠️ İLAVE SİPARİŞ ⚠️"
   *   olarak değiştirir. Telefon ilave akışında parent `true` geçer.
   * @param options.preFetchedTrendyolOrder **Trendyol oto-yazdırma fix** —
   *   yeni sipariş modal'ından tetiklenen oto kabul akışında `newTrendyolOrderData`
   *   doğrudan iletilir. Aksi halde `trendyolOrders` state listesi henüz
   *   güncellenmemiş olabileceği için sipariş bulunamaz ve fiş basılmaz.
   */
  const handleKitchenPrint = useCallback(async (
    orderId: number,
    source?: string,
    options?: {
      itemIdsToPrint?: number[];
      isAdditionalOrder?: boolean;
      preFetchedTrendyolOrder?: TrendyolOrder;
      /**
       * **Force cancel header** — caller (örn. iptal akışı) kesin biliyor.
       * Bu bayrak `true` geldiğinde `OrderStatus` alanına bakılmadan
       * `isCancellationOrder=true` set edilir. Race koşulları (cancel API
       * commit edildi ama getPackageOrderById eski snapshot döndü) veya
       * field adı/format farklılıklarına karşı güvenli.
       */
      forceCancellation?: boolean;
    },
  ) => {
    if (!companyId) {
      failPrint('Mutfak fişi: Şirket bilgisi bulunamadı');
      return;
    }
    
    if (!screenData?.printingSettings) {
      failPrint('Mutfak fişi: Yazdırma ayarları yapılandırılmamış. Lütfen ayarlar menüsünden yazıcı ayarlarını yapın.');
      return;
    }

    const selectedOrder = findPrintTargetOrder(filteredOrders, orderId, source);
    const effectiveSource = selectedOrder?.source || source || '';
    console.log('🖨️ [KitchenPrint] Sipariş seçimi:', { orderId, source, foundSource: selectedOrder?.source, effectiveSource, foundId: selectedOrder?.id });

    // Mutfak fişi de markaya özel başlıkla basılır; marka yoksa çözümleyici
    // ekran varsayılanını döndürür.
    const resolveHeader = (platform: string, raw?: any) =>
      resolveReceiptHeader(buildReceiptOrderRef(platform, selectedOrder, raw), screenData);
    if (effectiveSource === 'getir' || effectiveSource.includes('getir')) {
      console.log('🖨️ [KitchenPrint] Getir siparişi mutfağa yazdırılıyor...', { orderId });
      try {
        const result = (await getirApi.get(`/api/getir-orders/order/${orderId}`)).data;
        if (!result.success || !result.data) {
          failPrint(`Mutfak fişi: Getir sipariş detayı alınamadı (${result.message || 'Bilinmeyen hata'})`);
          return;
        }
        const getirDetail = result.data;

        const kitchenItems: PackageKitchenOrderItem[] = (getirDetail.items || []).map((item: any, index: number) => {
          const itemName = item.name || 'Ürün';
          const itemId = item.id?.toString() || '';
          let catInfo: { categoryId: string; categoryName: string } | undefined;
          if (platformCategoryMappingLookup) {
            catInfo = platformCategoryMappingLookup.get(`getir_pid_${itemId}`)
              || platformCategoryMappingLookup.get(`getir_${itemName.toLowerCase().trim()}`);
          }
          return {
            id: itemId || `getir-item-${index}`,
            name: itemName,
            quantity: item.quantity || 1,
            note: item.note || undefined,
            categoryId: catInfo?.categoryId,
            categoryName: catInfo?.categoryName,
            subItems: (item.options || []).map((opt: any, oi: number) => ({
              id: opt.id?.toString() || `getir-opt-${oi}`,
              name: opt.optionName || '',
              quantity: 1,
              isExtraOption: true,
            })),
          };
        });

        const checkoutDate = getirDetail.checkoutDate ? new Date(getirDetail.checkoutDate) : new Date();
        const deliveryLabel = getirDetail.deliveryType === 1 ? 'Getir Getirsin' : 'Restoran Getirsin';

        const notesParts = [
          getirDetail.clientNote,
          getirDetail.doNotKnock ? 'Kapıya vurma' : null,
          getirDetail.dropOffAtDoor ? 'Kapıya bırak' : null,
          deliveryLabel,
        ].filter(Boolean);

        const receiptData: PackageKitchenReceiptData = {
          orderNumber: `GETİR #${getirDetail.confirmationId || getirDetail.getirId?.substring(0, 8) || getirDetail.id}`,
          shortCode: getirDetail.confirmationId || undefined,
          customerName: `GETİR - ${getirDetail.clientName || 'Müşteri'}`,
          customerPhone: getirDetail.clientPhone || undefined,
          customerAddress: getirDetail.deliveryAddress || undefined,
          items: kitchenItems,
          orderNote: notesParts.join(' | ') || undefined,
          orderTime: checkoutDate.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Istanbul' }),
          isAdditionalOrder: false,
          isCancellationOrder: false,
          header: resolveHeader('getir', getirDetail),
        };

        console.log('🖨️ [KitchenPrint] Getir yazdırma verisi hazır:', receiptData);

        if (screenData.printingSettings.printingMode === 'category') {
          await printPackageKitchenReceiptByCategory(receiptData);
        } else {
          await printPackageKitchenReceipt(receiptData);
        }
      } catch (error: any) {
        if (error?.response?.status === 401) {
          console.warn('⚠️ [KitchenPrint] Token hatası (401) - token yenileme tetikleniyor...');
          refreshGetirToken();
          failPrint('Mutfak fişi: Getir token süresi dolmuş, yenileniyor. Lütfen tekrar deneyin.');
          return;
        }
        console.error('[KitchenPrint] Getir yazdirma hatasi:', error);
        failPrint(`Mutfak fişi (Getir) yazdırılamadı: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`);
      }
      return;
    }

    if (effectiveSource === 'migros' || effectiveSource.includes('migros')) {
      console.log('[KitchenPrint] Migros siparisi mutfaga yazdiriliyor...', { orderId });
      try {
        const result = (await migrosApi.get(`/api/migros-orders/order/${orderId}`)).data;
        if (!result.success || !result.data) { failPrint('Mutfak fişi: Migros sipariş detayı alınamadı'); return; }
        const migrosDetail = result.data;

        const buildMigrosKitchenSubItems = (options: any[]): PackageKitchenOrderSubItem[] => {
          if (!options || options.length === 0) return [];
          const topLevel = options.filter((o: any) => o.parentObjectOptionItemId === -1 || o.parentObjectOptionItemId === undefined);
          const subMap = new Map<number, any[]>();
          options.filter((o: any) => o.parentObjectOptionItemId !== -1 && o.parentObjectOptionItemId !== undefined).forEach((sub: any) => {
            const key = sub.parentObjectOptionItemId;
            if (!subMap.has(key)) subMap.set(key, []);
            subMap.get(key)!.push(sub);
          });
          const result: PackageKitchenOrderSubItem[] = [];
          const excludedTop = topLevel.filter((o: any) => o.excluded);
          if (excludedTop.length > 0) {
            const grouped = new Map<string, string[]>();
            excludedTop.forEach((e: any) => {
              const key = e.headerName || 'Malzemeler';
              if (!grouped.has(key)) grouped.set(key, []);
              grouped.get(key)!.push(e.itemNames || '');
            });
            grouped.forEach((items, header) => {
              result.push({ id: `ex-${header}`, name: `CIKARILDI (${header}): ${items.join(', ')}`, quantity: 1, isExtraOption: false });
            });
          }
          const includedTop = topLevel.filter((o: any) => !o.excluded);
          includedTop.forEach((opt: any, oi: number) => {
            const priceTL = ((opt.primaryDiscountedPrice || opt.primaryPrice || 0) / 100);
            const priceStr = priceTL > 0 ? ` +${priceTL.toFixed(2)}TL` : '';
            const qtyStr = (opt.quantity || 1) > 1 ? ` x${opt.quantity}` : '';
            result.push({
              id: opt.id?.toString() || `opt-${oi}`,
              name: `${opt.headerName}: ${opt.itemNames || ''}${qtyStr}${priceStr}`,
              quantity: 1,
              isExtraOption: true,
            });
            const subs = subMap.get(opt.objectOptionItemId) || [];
            const exSubs = subs.filter((s: any) => s.excluded);
            const inSubs = subs.filter((s: any) => !s.excluded);
            if (exSubs.length > 0) {
              const subGrouped = new Map<string, string[]>();
              exSubs.forEach((s: any) => {
                const k = s.headerName || 'Malzemeler';
                if (!subGrouped.has(k)) subGrouped.set(k, []);
                subGrouped.get(k)!.push(s.itemNames || '');
              });
              subGrouped.forEach((items, header) => {
                result.push({ id: `subex-${header}`, name: `  CIKARILDI (${header}): ${items.join(', ')}`, quantity: 1, isExtraOption: false });
              });
            }
            inSubs.forEach((sub: any, si: number) => {
              const sp = ((sub.primaryDiscountedPrice || sub.primaryPrice || 0) / 100);
              const spStr = sp > 0 ? ` +${sp.toFixed(2)}TL` : '';
              const sq = (sub.quantity || 1) > 1 ? ` x${sub.quantity}` : '';
              result.push({
                id: sub.id?.toString() || `sub-${si}`,
                name: `  > ${sub.headerName}: ${sub.itemNames || ''}${sq}${spStr}`,
                quantity: 1,
                isExtraOption: true,
              });
            });
          });
          return result;
        };

        const kitchenItems: PackageKitchenOrderItem[] = (migrosDetail.items || []).map((item: any, index: number) => {
          const itemName = item.name || 'Urun';
          const itemId = item.id?.toString() || '';
          let catInfo: { categoryId: string; categoryName: string } | undefined;
          if (platformCategoryMappingLookup) {
            // Eşleştirme platform ürün kimliğiyle kaydedilir (productId); satır kimliği (id) eski kayıtlar için yedek
            const migrosPid = (item.productId ?? item.ProductId)?.toString() || '';
            catInfo = (migrosPid ? platformCategoryMappingLookup.get(`migros_pid_${migrosPid}`) : undefined)
              || platformCategoryMappingLookup.get(`migros_pid_${itemId}`)
              || platformCategoryMappingLookup.get(`migros_${itemName.toLowerCase().trim()}`);
          }
          return {
            id: itemId || `migros-item-${index}`,
            name: itemName,
            quantity: item.amount || 1,
            note: item.note || undefined,
            categoryId: catInfo?.categoryId,
            categoryName: catInfo?.categoryName,
            subItems: buildMigrosKitchenSubItems(item.options || []),
          };
        });

        const deliveryLabel = migrosDetail.deliveryProvider === 'MIGROS' ? 'Migros Kurye' : 'Restoran Kuryesi';
        const notesParts = [
          migrosDetail.orderNote,
          deliveryLabel,
          migrosDetail.contactlessDelivery ? 'Temassiz teslimat' : null,
          migrosDetail.ringDoorBell === false ? 'KAPI ZILINI CALMA' : null,
          migrosDetail.saveGreen ? 'Cevreci paketleme' : null,
        ].filter(Boolean);

        const migrosCreatedAt = migrosDetail.createdAt || '';
        const migrosUtcStr = (!migrosCreatedAt.endsWith('Z') && !migrosCreatedAt.includes('+')) ? migrosCreatedAt + 'Z' : migrosCreatedAt;
        const migrosTimeStr = new Date(migrosUtcStr).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Istanbul' });

        const receiptData: PackageKitchenReceiptData = {
          orderNumber: `MIGROS #${migrosDetail.shortCode || migrosDetail.migrosOrderId || migrosDetail.id}`,
          shortCode: migrosDetail.shortCode || undefined,
          customerName: `MIGROS - ${migrosDetail.customerFullName || 'Musteri'}`,
          customerPhone: migrosDetail.customerPhoneNumber || undefined,
          customerAddress: [migrosDetail.addressDetail, migrosDetail.districtName, migrosDetail.townName].filter(Boolean).join(', ') || undefined,
          items: kitchenItems,
          orderNote: notesParts.join(' | ') || undefined,
          orderTime: migrosTimeStr,
          isAdditionalOrder: false,
          isCancellationOrder: false,
          header: resolveHeader('migros_yemek', migrosDetail),
        };

        if (screenData.printingSettings.printingMode === 'category') {
          await printPackageKitchenReceiptByCategory(receiptData);
        } else {
          await printPackageKitchenReceipt(receiptData);
        }
      } catch (error) {
        console.error('[KitchenPrint] Migros yazdirma hatasi:', error);
        failPrint(`Mutfak fişi (Migros) yazdırılamadı: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`);
      }
      return;
    }

    if (effectiveSource === 'trendyol' || effectiveSource.includes('trendyol')) {
      console.log('[KitchenPrint] Trendyol siparisi mutfaga yazdiriliyor...', { orderId, hasPreFetched: !!options?.preFetchedTrendyolOrder });
      try {
        // Oto-kabul akışında parent, modal'da elinde bulunan tam Trendyol
        // sipariş objesini (newTrendyolOrderData) doğrudan iletir. Sipariş henüz
        // `trendyolOrders` state listesine girmemiş olabileceği için listeyi
        // yalnızca fallback olarak kullanıyoruz.
        const trdOrder = options?.preFetchedTrendyolOrder
          || trendyolOrders.find(o => {
            const converted = convertTrendyolOrderToPackageOrder(o);
            return converted.id === orderId;
          });
        if (!trdOrder) {
          console.warn('[KitchenPrint] Trendyol sipariş detayı bulunamadı', {
            orderId,
            trendyolOrdersCount: trendyolOrders.length,
            hasPreFetched: !!options?.preFetchedTrendyolOrder,
          });
          failPrint('Mutfak fişi: Trendyol sipariş detayı bulunamadı');
          return;
        }

        console.log('[KitchenPrint] Trendyol ürün açıklamaları durumu:', {
          mapSize: trendyolProductDescriptions?.size || 0,
          lineProductIds: (trdOrder.lines || []).map(l => l.productId),
          matchedDescriptions: (trdOrder.lines || []).map(l => ({ productId: l.productId, name: l.name, hasDescription: !!trendyolProductDescriptions?.get(l.productId) })),
        });

        const kitchenItems: PackageKitchenOrderItem[] = (trdOrder.lines || []).map((line, index) => {
          const subItems: PackageKitchenOrderSubItem[] = [];

          if (line.modifierProducts?.length) {
            const flattenMods = (mods: any[], depth: number = 0): void => {
              for (const mod of mods) {
                subItems.push({
                  id: `trd-mod-${index}-${subItems.length}`,
                  name: `${mod.name}${mod.price > 0 ? ` (+${mod.price.toFixed(2)} TL)` : ''}`,
                  quantity: 1,
                  isExtraOption: true,
                });
                if (mod.modifierProducts?.length) flattenMods(mod.modifierProducts, depth + 1);
              }
            };
            flattenMods(line.modifierProducts);
          }

          if (line.extraIngredients?.length) {
            for (const extra of line.extraIngredients) {
              subItems.push({
                id: `trd-extra-${index}-${subItems.length}`,
                name: `+ ${extra.name}${extra.price > 0 ? ` (+${extra.price.toFixed(2)} TL)` : ''}`,
                quantity: 1,
                isExtraOption: true,
              });
            }
          }

          if (line.removedIngredients?.length) {
            for (const removed of line.removedIngredients) {
              subItems.push({
                id: `trd-removed-${index}-${subItems.length}`,
                name: `- ${removed.name} (ÇIKARILDI)`,
                quantity: 1,
                isExtraOption: false,
              });
            }
          }

          const desc = trendyolProductDescriptions?.get(line.productId);

          let catInfo: { categoryId: string; categoryName: string } | undefined;
          if (platformCategoryMappingLookup) {
            catInfo = platformCategoryMappingLookup.get(`trendyol_pid_${line.productId}`)
              || platformCategoryMappingLookup.get(`trendyol_${(line.name || '').toLowerCase().trim()}`);
          }

          // Trendyol her sipariş edilen adet için line.items dizisine bir eleman koyar.
          // Adisyon (fiş) tarafı da aynı kuralı kullanıyor (line 834), tutarlı tutuyoruz.
          const lineQty = (line as any).items?.length
            || (line as any).quantity
            || (line as any).Quantity
            || 1;

          return {
            id: `trd-item-${index}`,
            // Eskiden ürün açıklaması name'in 2. satırına `\n` ile ekleniyordu;
            // local-service `descFromName` fallback'iyle çıkarsa da kategori-bazlı
            // yolda escPosData mapping'i name'i kısa kestiği için description
            // güvenilir biçimde gözükmüyordu. Artık ayrı `description` alanına
            // taşındı — adisyon fişiyle de paralel.
            name: line.name || 'Ürün',
            description: desc || undefined,
            quantity: lineQty,
            note: undefined,
            categoryId: catInfo?.categoryId,
            categoryName: catInfo?.categoryName,
            subItems,
          };
        });

        const creationDate = new Date(trdOrder.packageCreationDate);
        const deliveryLabel = trdOrder.deliveryType === 'GO' ? 'Uber Eats Kurye' : 'Restoran Kuryesi';

        const notesParts = [
          trdOrder.customerNote,
          deliveryLabel,
          trdOrder.storePickupSelected ? 'Gel Al' : null,
        ].filter(Boolean);

        const receiptData: PackageKitchenReceiptData = {
          orderNumber: `TRENDYOL #${trdOrder.orderNumber || trdOrder.orderCode}`,
          shortCode: trdOrder.orderNumber || trdOrder.orderCode || undefined,
          customerName: `UBER EATS - ${trdOrder.customer?.firstName || ''} ${trdOrder.customer?.lastName || ''}`.trim(),
          customerPhone: trdOrder.address?.phone || trdOrder.callCenterPhone || undefined,
          customerAddress: trdOrder.address ? [trdOrder.address.address1, trdOrder.address.neighborhood, trdOrder.address.district, trdOrder.address.city].filter(Boolean).join(', ') : undefined,
          items: kitchenItems,
          orderNote: notesParts.join(' | ') || undefined,
          orderTime: creationDate.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Istanbul' }),
          isAdditionalOrder: false,
          isCancellationOrder: false,
          header: resolveHeader('trendyol_yemek', trdOrder),
        };

        console.log('[KitchenPrint] Trendyol yazdirma verisi hazir:', receiptData);

        if (screenData.printingSettings.printingMode === 'category') {
          await printPackageKitchenReceiptByCategory(receiptData);
        } else {
          await printPackageKitchenReceipt(receiptData);
        }
      } catch (error) {
        console.error('[KitchenPrint] Trendyol yazdirma hatasi:', error);
        failPrint(`Mutfak fişi (Trendyol) yazdırılamadı: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`);
      }
      return;
    }

    if (effectiveSource === 'yemeksepeti' || effectiveSource.includes('yemeksepeti')) {
      console.log('[KitchenPrint] Yemeksepeti siparisi mutfaga yazdiriliyor...', { orderId });
      try {
        const result = (await yemeksepetiApi.get(`/api/ys-orders/order/${orderId}`)).data;
        const ysSuccess = result.Success ?? result.success;
        const ysData = result.Data ?? result.data;
        if (!ysSuccess || !ysData) { failPrint('Mutfak fişi: Yemeksepeti sipariş detayı alınamadı'); return; }
        const ysDetail = ysData;
        const ysDetailItems = ysDetail.items ?? ysDetail.Items ?? [];

        let rawProducts: any[] = [];
        let rawDeliveryInstructions = '';
        try {
          const rawJson = ysDetail.rawJsonData ?? ysDetail.RawJsonData;
          if (rawJson) {
            const rawParsed = JSON.parse(rawJson);
            rawProducts = rawParsed?.products || [];
            rawDeliveryInstructions = rawParsed?.delivery?.address?.deliveryInstructions || '';
          }
        } catch {}

        const flattenOptions = (opt: any, prefix: string, depth: number = 0): { id: string; name: string; quantity: number; isExtraOption: boolean }[] => {
          const name = opt.name ?? opt.Name ?? '';
          const qty = opt.quantity ?? opt.Quantity ?? 1;
          const result = [{ id: prefix, name: `${'  '.repeat(depth)}${name}`, quantity: qty, isExtraOption: true }];
          const children: any[] = opt.children ?? opt.Children ?? [];
          children.forEach((child: any, ci: number) => {
            result.push(...flattenOptions(child, `${prefix}-c${ci}`, depth + 1));
          });
          return result;
        };

        const kitchenItems: PackageKitchenOrderItem[] = ysDetailItems.map((item: any, index: number) => {
          const rawProd = rawProducts[index];
          const dbToppings: any[] = (() => { try { const tj = item.toppingsJson ?? item.ToppingsJson; return tj ? JSON.parse(tj) : []; } catch { return []; } })();
          const rawToppings = rawProd?.selectedToppings || [];
          const rawChoices = rawProd?.selectedChoices || [];
          const toppings = dbToppings.length > 0 ? dbToppings : rawToppings;

          const subItems: { id: string; name: string; quantity: number; isExtraOption: boolean }[] = [];
          toppings.forEach((t: any, ti: number) => { subItems.push(...flattenOptions(t, `ys-top-${index}-${ti}`)); });
          rawChoices.forEach((c: any, ci: number) => { subItems.push(...flattenOptions(c, `ys-ch-${index}-${ci}`)); });

          const ysCatId = (item.categoryId ?? item.CategoryId)?.toString() || undefined;
          const ysCatName = item.categoryName ?? item.CategoryName ?? undefined;
          let finalCatId = ysCatId;
          let finalCatName = ysCatName;
          if (!finalCatId && platformCategoryMappingLookup) {
            const itemName = item.name ?? item.Name ?? '';
            const itemId = (item.id ?? item.Id)?.toString() || '';
            // Eşleştirme platform ürün kimliğiyle kaydedilir (productId); satır kimliği (id) eski kayıtlar için yedek
            const ysPid = (item.productId ?? item.ProductId)?.toString() || '';
            const catInfo = (ysPid ? platformCategoryMappingLookup.get(`yemeksepeti_pid_${ysPid}`) : undefined)
              || platformCategoryMappingLookup.get(`yemeksepeti_pid_${itemId}`)
              || platformCategoryMappingLookup.get(`yemeksepeti_${itemName.toLowerCase().trim()}`);
            if (catInfo) { finalCatId = catInfo.categoryId; finalCatName = catInfo.categoryName; }
          }

          const ysItemName = item.name ?? item.Name ?? 'Urun';
          // Yemeksepeti'de variation (örn. "Büyük Boy") ürün adından bağımsız
          // gelir; adisyon fişinde subItems içinde de görünür ama mutfakta
          // ürün adının altında küçük puntoyla `description` olarak çıksın.
          const ysVariationName = item.variationName ?? item.VariationName;
          const ysDescription = ysVariationName && ysVariationName !== ysItemName
            ? ysVariationName
            : undefined;

          return {
            id: (item.id ?? item.Id)?.toString() || `ys-item-${index}`,
            name: ysItemName,
            description: ysDescription,
            quantity: item.quantity ?? item.Quantity ?? 1,
            note: item.comment ?? item.Comment ?? rawProd?.comment ?? undefined,
            categoryId: finalCatId,
            categoryName: finalCatName,
            subItems,
          };
        });

        const deliveryLabel = getYsDeliveryLabel(ysDetail, 'receipt');
        const ysPickupTime = isYsPickupOrder(ysDetail)
          ? formatYsPickupTime(parseYsRawJsonData(ysDetail.rawJsonData ?? ysDetail.RawJsonData).pickupTime)
          : undefined;
        const customerComment = ysDetail.customerComment ?? ysDetail.CustomerComment;
        const notesParts = [
          customerComment,
          rawDeliveryInstructions && rawDeliveryInstructions !== customerComment ? `TESLIMAT: ${rawDeliveryInstructions}` : '',
          deliveryLabel,
          ysPickupTime ? `Teslim Alma Saati: ${ysPickupTime}` : '',
        ].filter(Boolean);

        const shortCode = ysDetail.shortCode ?? ysDetail.ShortCode;
        const code = ysDetail.code ?? ysDetail.Code;
        const ysId = ysDetail.id ?? ysDetail.Id;
        const customerFullName = ysDetail.customerFullName ?? ysDetail.CustomerFullName;
        const customerPhone = ysDetail.customerPhone ?? ysDetail.CustomerPhone;
        const deliveryAddress = ysDetail.deliveryAddress ?? ysDetail.DeliveryAddress;
        const deliveryDistrict = ysDetail.deliveryDistrict ?? ysDetail.DeliveryDistrict;
        const deliveryCity = ysDetail.deliveryCity ?? ysDetail.DeliveryCity;
        const createdAt = ysDetail.createdAt ?? ysDetail.CreatedAt;

        const receiptData: PackageKitchenReceiptData = {
          orderNumber: `Y.SEPETİ #${shortCode || code || ysId}`,
          shortCode: shortCode || undefined,
          customerName: `Y.SEPETİ - ${customerFullName || 'Musteri'}`,
          customerPhone: customerPhone || undefined,
          customerAddress: [deliveryAddress, deliveryDistrict, deliveryCity].filter(Boolean).join(', ') || undefined,
          items: kitchenItems,
          orderNote: notesParts.join(' | ') || undefined,
          orderTime: createdAt ? new Date(createdAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Istanbul' }) : '',
          isAdditionalOrder: false,
          isCancellationOrder: false,
          header: resolveHeader('yemeksepeti', ysDetail),
        };

        if (screenData.printingSettings.printingMode === 'category') {
          await printPackageKitchenReceiptByCategory(receiptData);
        } else {
          await printPackageKitchenReceipt(receiptData);
        }
      } catch (error) {
        console.error('[KitchenPrint] Yemeksepeti yazdirma hatasi:', error);
        failPrint(`Mutfak fişi (Yemeksepeti) yazdırılamadı: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`);
      }
      return;
    }
    
    const qrOrder = [...qrMenuOrders, ...ecommerceOrders, ...kioskOrders].find(o => o.id === orderId);
    if (qrOrder) {
      console.log('🖨️ [KitchenPrint] QR/Kiosk siparişi yazdırılıyor...', { orderId });
      
      const masaInfo = qrOrder.masaAdi || (qrOrder.masaNumarasi ? `Masa ${qrOrder.masaNumarasi}` : '');
      const orderNo = qrOrder.siparisId?.split('-').slice(-2).join('-') || qrOrder.siparisId || '';
      
      const receiptData: PackageKitchenReceiptData = {
        orderNumber: masaInfo ? `${masaInfo} - ${orderNo}` : orderNo,
        shortCode: orderNo,
        orderTime: new Date(qrOrder.siparisOlusturmaTarihi).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Istanbul' }),
        customerName: `Masa Siparişi - ${qrOrder.musteriAdi || qrOrder.kullaniciAdi || 'Anonim'}`,
        customerAddress: masaInfo || undefined,
        items: qrOrder.siparisDetaylari?.map((item: any, index: number) => ({
          id: item.urunId || `qr-item-${index}`,
          name: item.urunAdi,
          quantity: item.miktar || item.adet || 1,
          note: item.notlar || item.aciklama,
          categoryId: item.categoryId || undefined,
          categoryName: item.kategoriAdi || undefined,
        })) || [],
        orderNote: qrOrder.siparisNotu,
      };
      
      if (screenData.printingSettings.printingMode === 'category') {
        await printPackageKitchenReceiptByCategory(receiptData);
      } else {
        await printPackageKitchenReceipt(receiptData);
      }
      return;
    }
    
    try {
      console.log('🖨️ [KitchenPrint] Sipariş detayı çekiliyor...', { orderId, companyId });
      
      const response = await getPackageOrderById(orderId);
      
      if (!response.Success || !response.Data) {
        failPrint(`Mutfak fişi: Sipariş detayı alınamadı (${response.Message || 'Bilinmeyen hata'})`);
        return;
      }
      
      const orderData = response.Data;
      console.log('🖨️ [KitchenPrint] Sipariş detayı alındı:', orderData);

      // ▼ DELTA PRINT — İlave sipariş akışında parent yalnızca yeni eklenen
      //   item ID'lerini iletir. Bu durumda eski kalemleri yazdırmamak için
      //   item listesini filtreliyoruz. `itemIdsToPrint` undefined/boş ise
      //   eski davranış (tüm aktif item'lar) korunur.
      const allItems: any[] = orderData.Items || [];
      const itemIdsToPrint = options?.itemIdsToPrint;
      const filteredItems: any[] = (Array.isArray(itemIdsToPrint) && itemIdsToPrint.length > 0)
        ? allItems.filter((it: any) => itemIdsToPrint.includes(Number(it.Id)))
        : allItems;

      if (Array.isArray(itemIdsToPrint) && itemIdsToPrint.length > 0) {
        console.log('🖨️ [KitchenPrint] DELTA PRINT aktif:', {
          totalItems: allItems.length,
          itemIdsToPrint,
          filteredCount: filteredItems.length,
        });

        // Güvenlik: filtre sonucu boş ise yazdırmadan çık. Aksi halde boş bir
        // mutfak fişi basılabilir. Bu durum normalde backend `AddedItemIds`
        // döndürmediği eski sürümlerde gerçekleşebilir.
        if (filteredItems.length === 0) {
          console.warn('🖨️ [KitchenPrint] DELTA PRINT: filtre sonucu hiç item eşleşmedi, yazdırma atlanıyor');
          return;
        }
      }

      // ✅ KRİTİK: Telefon paket siparişlerinde kategori-bazlı yazdırma
      // **QuickSale ekranındaki ürün-kategori atamasını** kullanır,
      // ürünün stok kartındaki orijinal `InventoryItem.CategoryId` değerini DEĞİL.
      //
      // Sebep: Kullanıcı SettingsModal'da yazıcıları QuickSale ekranına atanmış
      // kategorilere göre eşliyor (örn. "Lahmacun" ekran kategorisi → KITCHEN-1).
      // Aynı ürün başka QuickSale ekranlarında farklı bir kategoriye atanmış
      // olabilir; stok kartının orijinal kategorisi ise yazıcı atamasında
      // muhtemelen hiç listelenmemiştir. Bu yüzden override haritası birinci
      // öncelik. Haritada yoksa categoryId `undefined` bırakılır — ürün
      // varsayılan adisyon/genel yazıcıya düşer ve kullanıcı uyarılır.
      const productScreenCategoryMap: Record<string, string> =
        (screenData?.productScreenCategoryMap as Record<string, string>) || {};

      const kitchenItems: PackageKitchenOrderItem[] = filteredItems.map((item: any) => {
        const productId = item.ProductId ? String(item.ProductId) : '';
        const screenCategoryId = productId ? productScreenCategoryMap[productId] : undefined;

        return {
          id: item.Id?.toString() || item.ProductId?.toString() || '',
          name: item.ProductName || 'Ürün',
          quantity: item.Quantity || 1,
          // PackageOrderItemDto.Notes (PascalCase). Eski sürümlerde
          // `item.ItemNote` arıyordu; backend'in dönen alan adıyla
          // uyumsuz olduğu için ürün notu mutfak fişine hiç çıkmıyordu.
          // `notes` (camelCase) alternatifi de bazı proxy/serileştirme
          // katmanları için yedek olarak okunur.
          note: item.Notes || item.notes || undefined,
          // 1. öncelik: QuickSale ekranı-spesifik atama
          // 2. öncelik YOK — eski `CategoryGuid` (stok kartı kategorisi)
          //    bilerek kullanılmıyor; aksi halde yanlış yazıcıya yönlenebilir.
          categoryId: screenCategoryId || undefined,
          categoryName: item.CategoryName || undefined,
          subItems: [],
        };
      });

      // 🔬 TANI LOG: Hangi ürünün QuickSale haritasında bulunduğunu /
      // bulunmadığını gösterir. Boş kalan ürünler varsayılan yazıcıya düşer.
      filteredItems.forEach((raw: any, idx: number) => {
        const pid = raw.ProductId ? String(raw.ProductId) : '';
        const overrideCat = pid ? productScreenCategoryMap[pid] : undefined;
        console.log(`🔬 [KitchenPrint][Item#${idx + 1}] "${raw.ProductName}"`, {
          ProductId: raw.ProductId || '(YOK)',
          QuickSaleEkranKategorisi: overrideCat || '(YOK — ürün ekranda atanmamış, varsayılan yazıcıya düşecek)',
          StokKartiKategorisi_kullanilmiyor: raw.CategoryGuid || null,
        });
      });
      
      const orderStatusRaw =
        orderData.OrderStatus ??
        orderData.orderStatus ??
        '';
      const statusUpper =
        typeof orderStatusRaw === 'string' ? orderStatusRaw.toUpperCase().trim() : '';

      const receiptData: PackageKitchenReceiptData = {
        orderNumber: orderData.OrderNumber || `PS-${orderId}`,
        shortCode: orderData.ShortCode || undefined,
        customerName: orderData.AccountName || 'Müşteri',
        customerPhone: orderData.CustomerPhone || undefined,
        customerAddress: orderData.CustomerAddress || undefined,
        items: kitchenItems,
        orderNote: orderData.Notes || undefined,
        companyName: undefined,
        orderTime: orderData.CreatedAt ? new Date(orderData.CreatedAt).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : undefined,
        isAdditionalOrder: options?.isAdditionalOrder === true,
        // Caller explicit olarak iptal bildirdiyse (örn. iptal akışı) bu öncelikli.
        // Aksi halde backend snapshot'tan oku — case-insensitive + OrderStatus/orderStatus (Pascal/camel).
        isCancellationOrder:
          options?.forceCancellation === true ||
          statusUpper === 'CANCELLED',
      };
      
      console.log('🖨️ [KitchenPrint] Yazdırma verisi hazır:', receiptData);
      
      if (screenData.printingSettings.printingMode === 'category') {
        await printPackageKitchenReceiptByCategory(receiptData);
      } else {
        await printPackageKitchenReceipt(receiptData);
      }
      
    } catch (error) {
      console.error('🖨️ [KitchenPrint] Hata:', error);
      failPrint(`Mutfak fişi yazdırılamadı: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`);
    }
  }, [companyId, screenData?.printingSettings, screenData?.platformIntegrations, printPackageKitchenReceipt, printPackageKitchenReceiptByCategory, filteredOrders, trendyolOrders, qrMenuOrders, ecommerceOrders, kioskOrders, refreshGetirToken, failPrint]);

  /**
   * Paket sipariş adisyon (müşteri fişi) yazdırır.
   *
   * @param options.preFetchedTrendyolOrder **Trendyol oto-yazdırma fix** —
   *   oto kabul akışında parent, elindeki tam Trendyol sipariş objesini
   *   doğrudan iletir. Aksi halde `trendyolOrders` state'i henüz güncellenmemiş
   *   olabileceği için sipariş bulunamaz ve fiş "ürün" placeholder ile / hiç
   *   basılmaz.
   */
  const handleReceiptPrint = useCallback(async (
    orderId: number,
    source?: string,
    options?: { preFetchedTrendyolOrder?: TrendyolOrder },
  ) => {
    if (!companyId) {
      failPrint('Sipariş fişi: Şirket bilgisi bulunamadı');
      return;
    }

    const printer = screenData?.printingSettings?.defaultReceiptPrinter
      || screenData?.printingSettings?.adisyonPrinter;
    if (!printer) {
      failPrint('Sipariş fişi: Fiş yazıcısı tanımlı değil. Lütfen ayarlardan bir yazıcı seçin.');
      return;
    }

    const selectedOrder = findPrintTargetOrder(filteredOrders, orderId, source);
    const effectiveSource = selectedOrder?.source || source || '';
    
    try {
      console.log('🧾 [ReceiptPrint] Sipariş fişi yazdırılıyor...', { orderId, source: effectiveSource, companyId });
      // Marka bazlı başlık: siparişin entegrasyonuna özel başlık varsa o,
      // yoksa ekran varsayılanı. Her platform dalı kendi ham siparişiyle
      // çözümleyiciyi çağırır; ortak alanlar tek yerde birleşir.
      const resolveHeader = (platform: string, raw?: any) =>
        resolveReceiptHeader(buildReceiptOrderRef(platform, selectedOrder, raw), screenData);
      const resolveLabel = (baseLabel: string, platform: string, raw?: any) =>
        resolveReceiptLabel(baseLabel, buildReceiptOrderRef(platform, selectedOrder, raw), screenData);
      let receiptData: PackageReceiptData | null = null;

      if (effectiveSource.includes('getir')) {
        const result = (await getirApi.get(`/api/getir-orders/order/${orderId}`)).data;
        if (!result.success || !result.data) {
          failPrint('Sipariş fişi: Getir sipariş detayı alınamadı');
          return;
        }
        const d = result.data;
        // ✅ Manuel modal (`GetirOrderDetailModal.handlePrintReceipt`) ile **aynı**
        // helper'ı kullanıyoruz. Eski kod opsiyon adlarını yalnızca `optionName`
        // alanından okuyor, `+`/`(+price)` format'ını uygulamıyor ve `it.note`
        // alanını fişe yansıtmıyordu — sonuçta adisyonda ürünün opsiyon /
        // not detayları boş görünüyordu. Helper modal'daki zengin mapping'i
        // birebir koruyor.
        const items: PackageReceiptItem[] = buildGetirReceiptItems(d);

        // ─── Tarih kaynagi — manuel modal `:376,400-401` birebir
        // ESKI BUG: `createdAt` (DB yazma ani / webhook gelisi) okunuyordu;
        // manuel `checkoutDate` (Getir onay ani) kullaniyor. Webhook
        // gecikmesinde 1-3 dakika fark ortaya cikiyordu. `checkoutDate`
        // oncelik, `createdAt` fallback; auto'nun TZ'i (Istanbul) korundu.
        const orderDate = d.checkoutDate
          ? new Date(d.checkoutDate)
          : (d.createdAt ? new Date(d.createdAt) : new Date());

        // ─── Teslimat etiketi — manuel modal `:377` birebir (permissive default)
        // ESKI BUG: `deliveryType !== 1 && !== 2` -> `undefined` -> template
        // etiket basmiyordu. Manuel her zaman bir label dondurur.
        const getirDeliveryLabel = d.deliveryType === 1 ? 'Getir Getirsin' : 'Restoran Getirsin';

        // ─── Adres bloku — manuel modal `:383-388` birebir
        // ESKI BUG: `deliveryAddress` bos siparislerde adres tamamen
        // kayboluyordu. Manuel parcalardan (district/city/aptNo/floor/doorNo)
        // toplayarak fallback uretiyor.
        let getirAddrStr = '';
        if (d.deliveryAddress) {
          getirAddrStr = d.deliveryAddress;
        } else {
          getirAddrStr = [
            d.district,
            d.city !== d.district ? d.city : null,
          ].filter(Boolean).join(', ');
        }

        // ─── Notlar — manuel modal `:378-382` birebir
        // ESKI BUG: sadece `clientNote` okunuyor, `doNotKnock` /
        // `dropOffAtDoor` bayraklari kayboluyordu (kurye icin kritik).
        // `customerNote` alani template'te (`PackageReceiptPrintService.tsx:387`)
        // render edilmiyor; sadece `orderNote` render ediliyor — bu yuzden
        // notlari `orderNote` alanina yaziyoruz. (Manuel modal'in `customerNote`
        // alanindaki gizli bug ayrica `:411` satirinda rename edildi.)
        const getirNotesParts = [
          d.clientNote,
          d.doNotKnock ? 'Kapıya vurma' : null,
          d.dropOffAtDoor ? 'Kapıya bırak' : null,
        ].filter(Boolean);

        // ─── Odeme metodu — manuel UI chip `:573` ile uyumlu
        // ESKI BUG: `paymentMethod === 3` (Kapida Kart) ana print'te ayrilmamisti;
        // sadece `1 -> Online`, digerleri -> "Kapida Odeme" basitlestirmesi
        // vardi. Manuel UI chip dogru ayrim yapiyordu, `handlePrintReceipt`
        // yapmiyordu. Manuel modal'da da ayni fix `:407` satirinda uygulandi.
        const getirIsOnlinePaid = d.paymentMethod === 1;
        const getirPaymentLabel = d.paymentMethodText
          || (d.paymentMethod === 1 ? 'Online Ödeme'
            : d.paymentMethod === 3 ? 'Kapıda Kart'
            : 'Kapıda Nakit');

        receiptData = {
          // Manuel modal `:394` birebir — `getirId.substring(0,8)` kisa hash
          // fallback eklendi (musteri/operatorun birbirine soyleyebilecegi
          // benzersiz kod).
          orderNumber: d.confirmationId || d.getirId?.substring(0, 8) || `G-${orderId}`,
          // Trendyol/YS pattern'i ile platform tutarliligi (template
          // `data.shortCode || data.orderNumber` onceligi kullanir).
          shortCode: d.confirmationId || undefined,
          date: orderDate.toLocaleDateString('tr-TR', { timeZone: 'Europe/Istanbul' }),
          time: orderDate.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Istanbul' }),
          // Manuel modal `:395` birebir — fallback metni `'Müşteri'`
          customerName: d.clientName || 'Müşteri',
          customerPhone: d.clientPhone || undefined,
          customerAddress: getirAddrStr || undefined,
          // Getir API'den dönen yapısal alanlar — şablon `binaNo/kat/daireNo`'yu
          // ayrı satırda basıyor (PackageReceiptPrintService.tsx:379).
          binaNo: d.aptNo || undefined,
          kat: d.floor || undefined,
          daireNo: d.doorNo || undefined,
          // Manuel modal `:398` — adres tarifi mavi kutuda template
          // (`PackageReceiptPrintService.tsx:380`) tarafindan basilir.
          addressDescription: d.addressDescription || undefined,
          items,
          subtotal: d.totalPrice || 0,
          vatTotal: 0,
          grandTotal: (d.totalDiscountedPrice != null && d.totalDiscountedPrice > 0 && d.totalDiscountedPrice !== d.totalPrice)
            ? d.totalDiscountedPrice : (d.totalPrice || 0),
          discountTotal: (d.totalDiscountedPrice != null && d.totalDiscountedPrice > 0 && d.totalDiscountedPrice !== d.totalPrice)
            ? (d.totalPrice - d.totalDiscountedPrice) : 0,
          // Trendyol/YS pattern'i ile platform tutarliligi (online ise PAID)
          paymentStatus: getirIsOnlinePaid ? 'PAID' : undefined,
          paymentMethod: getirPaymentLabel,
          isOnlinePaid: getirIsOnlinePaid,
          receiptLabel: resolveLabel('GETIR PAKET SIPARIS', 'getir', d),
          deliveryType: getirDeliveryLabel,
          // Notlar `orderNote` alanina yaziliyor (template'in bekledigi alan).
          // `customerNote` alani kaldirildi — template render etmiyor.
          orderNote: getirNotesParts.length > 0 ? getirNotesParts.join(' | ') : undefined,
          ...resolveHeader('getir', d),
          // Ileri tarihli siparis kutusu icin gerekli alanlar (manuel `:420-421`).
          // ESKI BUG: bu iki alan hic set edilmiyordu -> template ileri
          // tarihli kutusu (`PackageReceiptPrintService.tsx:357-372`) basilmiyordu;
          // kurye/mutfak teslim saatini bilmiyordu.
          isPreOrder: d.isScheduled || false,
          expectedDeliveryTime: d.scheduledDate || undefined,
        };

      } else if (effectiveSource.includes('yemeksepeti')) {
        const result = (await yemeksepetiApi.get(`/api/ys-orders/order/${orderId}`)).data;
        const ysSuccess = result.Success ?? result.success;
        const ysData = result.Data ?? result.data;
        if (!ysSuccess || !ysData) {
          failPrint('Sipariş fişi: Yemeksepeti sipariş detayı alınamadı');
          return;
        }
        const d = ysData;
        // ✅ Manuel modal ile aynı kaynaktan ürün satırlarını üret. Modal'daki
        // (YemeksepetiOrderDetailModal.handlePrintReceipt) zengin mantık —
        // it.name / quantity / unitPrice / paidPrice + toppings + choices +
        // indirimler + müşteri yorumu — tek helper'da toplandı. Eski kod
        // backend'in döndürmediği `productName`/`count` alanlarını okumaya
        // çalıştığı için adisyonda ürünler "Ürün" placeholder olarak
        // basılıyordu; helper alan adlarını doğru çözümlüyor.
        const items: PackageReceiptItem[] = buildYemeksepetiReceiptItems(d);
        // Adres ve teslimat-talimatları modal'daki `handlePrintReceipt`
        // (YemeksepetiOrderDetailModal.tsx:567,576,609) ile **aynı** kaynaklardan
        // okunur. Eski hook kodu backend'in döndürmediği `customerAddress` /
        // `CustomerAddress` alanlarını arıyordu → oto-yazdırılan adisyonda adres
        // tamamen boş kalıyordu.
        const ysAddrFull = [
          d.deliveryAddress ?? d.DeliveryAddress,
          d.deliveryDistrict ?? d.DeliveryDistrict,
          d.deliveryCity ?? d.DeliveryCity,
        ].filter(Boolean).join(', ');
        const ysRawParsed = parseYsRawJsonData(d.rawJsonData ?? d.RawJsonData);

        // ─── Kurye konum QR'ı ──────────────────────────────────────────────
        // `rawJsonData.delivery.address.{latitude,longitude}` alanlarından
        // Google Maps URL'i üretip QR'a çeviriyoruz. Kurye fişten okutarak
        // doğrudan navigasyona başlayabilir. Lat/lng yoksa QR basılmaz.
        let ysQrCodeDataUrl: string | undefined;
        let ysQrCodeRawUrl: string | undefined;
        const ysMapsUrl = buildGoogleMapsUrl(ysRawParsed.latitude, ysRawParsed.longitude);
        if (ysMapsUrl) {
          try {
            ysQrCodeRawUrl = ysMapsUrl;
            ysQrCodeDataUrl = await QRCode.toDataURL(ysMapsUrl, {
              errorCorrectionLevel: 'M',
              margin: 1,
              width: 320,
            });
          } catch (qrErr) {
            console.warn('🧾 [ReceiptPrint][YS] QR uretilemedi, QR siz basilacak:', qrErr);
          }
        }

        // ─── Tarih/saat — manuel modal (YemeksepetiOrderDetailModal.tsx:591-601)
        // Backend `createdAt` `Z` suffix'siz gelebiliyor; `parseAsUtcDate` UTC
        // olarak yorumlayip Istanbul TZ'de formatliyor. Eski kod hic var
        // olmayan `approvedDate` alanini okudugu icin hep `new Date()` (yani
        // YAZDIRMA ani) basiyordu — modal ile uyusmuyordu.
        const ysCreatedAt = parseAsUtcDate(d.createdAt ?? d.CreatedAt);
        const ysDateStr = ysCreatedAt
          ? new Intl.DateTimeFormat('tr-TR', {
              day: '2-digit', month: '2-digit', year: 'numeric', timeZone: ISTANBUL_TZ,
            }).format(ysCreatedAt)
          : '-';
        const ysTimeStr = ysCreatedAt
          ? new Intl.DateTimeFormat('tr-TR', {
              hour: '2-digit', minute: '2-digit', hour12: false, timeZone: ISTANBUL_TZ,
            }).format(ysCreatedAt)
          : '-';

        // ─── Odeme tespiti — manuel modal (YemeksepetiOrderDetailModal.tsx:569-572)
        // Eski kod backend'in DONDURMEDIGI `paymentMethodDescription` /
        // `paymentMethodId` alanlarini okuyordu; sonuc her zaman bos string +
        // false. Dogru alan `paymentType` (entity: `YemeksepetiOrder.PaymentType`).
        const ysPaymentTypeRaw = (d.paymentType ?? d.PaymentType ?? '') as string;
        const ysPtUpper = ysPaymentTypeRaw
          .toUpperCase()
          .replace(/İ/g, 'I')
          .replace(/Ş/g, 'S')
          .replace(/Ğ/g, 'G');
        const ysIsOnlinePaid = ysPtUpper.includes('ONLINE')
          || ysPtUpper.includes('PAY_WITH_CARD')
          || ysPtUpper.startsWith('PAY_WITH');

        const ysDeliveryLabel = getYsDeliveryLabel(d, 'receipt');
        // Gel Al siparişinde adres yoktur; müşterinin geleceği saat NOT satırına eklenir.
        const ysPickupTimeQs = isYsPickupOrder(d)
          ? formatYsPickupTime(ysRawParsed.pickupTime)
          : undefined;
        const ysPickupNote = isYsPickupOrder(d)
          ? `GEL AL${ysPickupTimeQs ? ` - Teslim Alma Saati: ${ysPickupTimeQs}` : ''}`
          : undefined;

        // ─── Toplamlar — manuel modal (YemeksepetiOrderDetailModal.tsx:585-587,636-638)
        // birebir ayni formul. Modal UI'inda kullanicinin gozuyle gordugu
        // `order.grandTotal` tek gercek kaynaktir; manuel fis ve oto-fis ayni
        // bu deger uzerinden basilir. Eski self-calculated formul (rawJson +
        // OrderDiscountsJson + Math.max cap) kaldirildi: backend timing'inden
        // bagimsiz olmaktan kazanilan iyilik, kullaniciya gosterilen tutardan
        // farkli fis basmak demekti — bu kullaniciya gore daha buyuk bir bug.
        const ysItemsTotal = items.reduce((sum, it) => sum + (it.totalPrice || 0), 0);
        const ysGrandTotal = Number(d.grandTotal ?? d.GrandTotal ?? 0) || 0;
        const ysDiscountTotal = ysItemsTotal > ysGrandTotal ? ysItemsTotal - ysGrandTotal : 0;
        const ysSubtotal = ysItemsTotal > ysGrandTotal ? ysItemsTotal : ysGrandTotal;

        // ─── Pre-order bayragi ve musteri notu (manuel :643,651,657-658)
        const ysIsPreOrder = !!(d.isPreOrder ?? d.IsPreOrder);
        const ysCustomerNoteBase = d.customerComment
          ?? d.CustomerComment
          ?? ysRawParsed.customerComment
          ?? undefined;
        const ysCustomerNote = ysPickupNote
          ? [ysPickupNote, ysCustomerNoteBase].filter(Boolean).join(' | ')
          : ysCustomerNoteBase;

        receiptData = {
          orderNumber: d.shortCode || d.ShortCode || d.code || d.Code || d.displayOrderId || d.DisplayOrderId || `YS-${orderId}`,
          shortCode: d.shortCode || d.ShortCode || d.code || d.Code || undefined,
          date: ysDateStr,
          time: ysTimeStr,
          // Modal (`YemeksepetiOrderDetailModal.handlePrintReceipt`) ile aynı
          // alan adlarını öncelikli olarak okuyoruz; eski `customerName` /
          // `customerPhoneNumber` fallback olarak kalıyor.
          customerName: d.customerFullName || d.CustomerFullName || d.customerName || d.CustomerName || 'Yemeksepeti Müşteri',
          customerPhone: d.customerPhone || d.CustomerPhone || d.customerPhoneNumber || d.CustomerPhoneNumber || undefined,
          customerAddress: ysAddrFull || undefined,
          addressDescription: ysRawParsed.deliveryInstructions || undefined,
          qrCodeDataUrl: ysQrCodeDataUrl,
          qrCodeRawUrl: ysQrCodeRawUrl,
          qrCodeLabel: (ysQrCodeDataUrl || ysQrCodeRawUrl) ? 'ADRES QR' : undefined,
          items,
          subtotal: ysSubtotal,
          vatTotal: 0,
          grandTotal: ysGrandTotal,
          discountTotal: ysDiscountTotal,
          // Manuel modal :640-642 ile birebir ayni:
          //   - Online odendiyse paymentStatus='PAID' (servis "ÖDENDİ" damgasini basiyor)
          //   - paymentMethod ya 'Online Ödeme' ya da ham paymentType (örn. 'CASH', 'PAY_WITH_CARD')
          paymentStatus: ysIsOnlinePaid ? 'PAID' : undefined,
          paymentMethod: ysIsOnlinePaid ? 'Online Ödeme' : (ysPaymentTypeRaw || 'Kapıda Ödeme'),
          isOnlinePaid: ysIsOnlinePaid,
          // Pre-order siparislerde modal ozel etiket basiyor (manuel :651)
          receiptLabel: resolveLabel(
            ysIsPreOrder ? 'YEMEKSEPETI - İLERİ TARİHLİ SİPARİŞ' : 'YEMEKSEPETI PAKET SIPARIS',
            'yemeksepeti',
            d,
          ),
          deliveryType: ysDeliveryLabel,
          // Manuel modal `orderNote` set etmiyor; sadece `customerNote` (manuel :643)
          customerNote: ysCustomerNote,
          ...resolveHeader('yemeksepeti', d),
          // Pre-order ek alanlari (manuel :657-658)
          isPreOrder: ysIsPreOrder,
          expectedDeliveryTime: d.expectedDeliveryTime ?? d.ExpectedDeliveryTime,
        };

      } else if (effectiveSource.includes('migros')) {
        const result = (await migrosApi.get(`/api/migros-orders/order/${orderId}`)).data;
        // Migros endpoint `{ Success: true, Data: ... }` PascalCase serialize
        // edebiliyor; iki formatti da destekliyoruz.
        const mData = result.Data ?? result.data ?? result;
        if (!mData) {
          failPrint('Sipariş fişi: Migros sipariş detayı alınamadı');
          return;
        }
        const d = mData;

        // ─── Items, totals, address, notes — manuel modal mantigi
        // (`MigrosOrderDetailModal.handlePrintReceipt`) ile birebir uyumlu
        // ortak helper'da toplandi (`utils/migrosReceiptBuilder.ts`).
        // ESKI BUG'lar:
        //   - Tutarlar `/100` (kurus->TL) bolunmuyordu -> 100 kat sisik
        //   - `grandTotal` `discountedPrice`'i goz ardi ediyordu
        //   - `discountTotal` var olmayan `d.discountTotal` alanindan
        //     okunuyordu -> hep 0
        //   - `item.note` urun-bazli not subItems'e eklenmiyordu
        //   - `customerAddress` `cityName` parcasini birlestirmiyordu
        //   - `orderNote` sadece ham `clientNote`'u aliyordu;
        //     `contactlessDelivery`/`ringDoorBell=false`/`saveGreen`
        //     bayraklari fise yansimiyordu (kurye icin kritik)
        const items: PackageReceiptItem[] = buildMigrosReceiptItems(d);
        const mgTotals = calculateMigrosTotals(d);
        const mgCustomerAddress = buildMigrosCustomerAddress(d);
        const mgOrderNote = buildMigrosOrderNote(d);

        // ─── Tarih kaynagi — YS/Getir pattern'i
        // ESKI BUG: olu fallback'ler (`d.createdDate`, `d.orderDate`) +
        // raw `new Date()` -> backend `Z` suffix'siz gonderirse yereldeki
        // saat dilimine gore yorumlanir.
        const mgCreatedAt = parseAsUtcDate(d.createdAt) || new Date();
        const mgDateStr = new Intl.DateTimeFormat('tr-TR', {
          day: '2-digit', month: '2-digit', year: 'numeric', timeZone: ISTANBUL_TZ,
        }).format(mgCreatedAt);
        const mgTimeStr = new Intl.DateTimeFormat('tr-TR', {
          hour: '2-digit', minute: '2-digit', hour12: false, timeZone: ISTANBUL_TZ,
        }).format(mgCreatedAt);

        // Teslimat etiketi — manuel modal `:414` ile birebir
        const mgDeliveryProvider = (d.deliveryProvider || '').toUpperCase();
        const mgDeliveryLabel = mgDeliveryProvider === 'MIGROS' ? 'Migros Kurye' : 'Restoran Kuryesi';

        // ─── Odeme tespiti — manuel modal `:438-439` ile birebir
        // ESKI BUG: backend'in net `isOnlinePayment: bool` field'i ihmal
        // edilip `paymentType` string'inde "online"/"credit" kelimesi
        // araniyor (heuristik); paymentType="POS" gibi degerlerde online
        // damgasi kayboluyor, kasiyer odeme almaya calisir.
        const mgIsOnlinePaid = !!d.isOnlinePayment;
        const mgPaymentLabel = d.paymentType || (mgIsOnlinePaid ? 'Online Ödeme' : 'Kapıda Ödeme');

        receiptData = {
          // Manuel modal `:428` birebir — `migrosOrderId` (Migros tarafi
          // benzersiz id) `MG-${frontend id}` fallback'inden onceliklidir.
          orderNumber: d.shortCode || String(d.migrosOrderId || d.id || orderId),
          // Trendyol/YS/Getir pattern'i: shortCode template'te
          // `data.shortCode || data.orderNumber` onceligi kullanir.
          shortCode: d.shortCode || undefined,
          date: mgDateStr,
          time: mgTimeStr,
          customerName: d.customerFullName || 'Müşteri',
          customerPhone: d.customerPhoneNumber || undefined,
          customerAddress: mgCustomerAddress,
          items,
          subtotal: mgTotals.subtotal,
          vatTotal: 0,
          grandTotal: mgTotals.grandTotal,
          discountTotal: mgTotals.discountTotal,
          // Manuel modal `:438-439` ile birebir + YS/Trendyol/Getir
          // pattern'i (online ise `paymentStatus: 'PAID'` -> "ÖDENDİ" damgasi)
          paymentStatus: mgIsOnlinePaid ? 'PAID' : undefined,
          paymentMethod: mgPaymentLabel,
          isOnlinePaid: mgIsOnlinePaid,
          receiptLabel: resolveLabel('MIGROS PAKET SIPARIS', 'migros_yemek', d),
          deliveryType: mgDeliveryLabel,
          // Notlar `orderNote` alanina yaziliyor (template'in bekledigi
          // alan; `customerNote` template tarafindan render edilmiyor).
          // `customerNote` satiri kaldirildi — Getir fix pattern'i.
          orderNote: mgOrderNote,
          ...(() => {
            const header = resolveHeader('migros_yemek', d);
            // Manuel modal `:446-447` ile birebir — default'lar (cok buyuk
            // logo basilma riskini onler).
            return {
              ...header,
              logoMaxWidthPx: header.logoMaxWidthPx ?? 384,
              logoMaxHeightPx: header.logoMaxHeightPx ?? 150,
            };
          })(),
        };

      } else if (effectiveSource.includes('trendyol')) {
        // 🐛 ESKİ BUG: `o.id === String(orderId)` — `o.id` Trendyol package id
        // (uzun string, ör. "1234567890123"), `orderId` ise
        // `parseInt(order.orderId)` (sayısal). Bu iki alan farklı olduğu için
        // karşılaştırma asla eşleşmiyor ve oto-yazdırma Trendyol siparişlerinde
        // sessizce atlanıyordu. handleKitchenPrint ile aynı yöntemi kullanıyoruz:
        // convertTrendyolOrderToPackageOrder ile dönüştürüp sayısal id'leri
        // karşılaştırıyoruz. Ek olarak parent oto-kabul akışında elinde olan
        // tam Trendyol objesini `options.preFetchedTrendyolOrder` ile iletebilir;
        // state listesi henüz güncellenmemişse fallback olur.
        const tOrder = options?.preFetchedTrendyolOrder
          || trendyolOrders.find(o => {
            const converted = convertTrendyolOrderToPackageOrder(o);
            return converted.id === orderId;
          });
        if (!tOrder) {
          console.warn('🧾 [ReceiptPrint] Trendyol sipariş bulunamadı, atlanıyor', {
            orderId,
            trendyolOrdersCount: trendyolOrders.length,
            hasPreFetched: !!options?.preFetchedTrendyolOrder,
          });
          failPrint('Sipariş fişi: Trendyol siparişi bulunamadı');
          return;
        }
        console.log('[ReceiptPrint] Trendyol ürün açıklamaları durumu:', {
          mapSize: trendyolProductDescriptions?.size || 0,
          lineProductIds: (tOrder.lines || []).map(l => l.productId),
        });

        // ─── items[] mapping — manuel modal (TrendyolOrderDetailModal.tsx:142-173) birebir
        const items: PackageReceiptItem[] = (tOrder.lines || []).map((line) => {
          const subs: { name: string }[] = [];
          if (line.modifierProducts?.length) {
            // Manuel modal `flattenModifiers` (TrendyolOrderDetailModal.tsx:83-92) birebir:
            // her modifier `depth` ile birlikte flat list'e dokulur, sonra
            // `'  '.repeat(depth) + '+ ' + name + (price>0 ? ` (+price)` : '')` formati uygulanir.
            const flatMods: { name: string; price: number; depth: number }[] = [];
            const flatten = (mods: any[], depth: number): void => {
              for (const mod of mods) {
                flatMods.push({ name: mod.name, price: mod.price ?? 0, depth });
                if (mod.modifierProducts?.length) flatten(mod.modifierProducts, depth + 1);
              }
            };
            flatten(line.modifierProducts, 0);
            for (const mod of flatMods) {
              subs.push({
                name: `${'  '.repeat(mod.depth)}+ ${mod.name}${mod.price > 0 ? ` (+${mod.price.toFixed(2)})` : ''}`,
              });
            }
          }
          if (line.extraIngredients?.length) {
            for (const extra of line.extraIngredients) {
              subs.push({ name: `+ ${extra.name}${extra.price > 0 ? ` (+${extra.price.toFixed(2)})` : ''}` });
            }
          }
          if (line.removedIngredients?.length) {
            for (const removed of line.removedIngredients) {
              subs.push({ name: `- ${removed.name} (çıkarıldı)` });
            }
          }
          const lineDesc = trendyolProductDescriptions?.get(line.productId);
          // Iptal edilmis alt-kalemleri haric tut (manuel :162)
          const qty = line.items?.filter((i: any) => !i.isCancelled).length || 1;
          // unitSellingPrice oncelikli, price fallback (manuel :169-170)
          const unit = line.unitSellingPrice || line.price || 0;
          return {
            name: line.name || 'Ürün',
            description: lineDesc || undefined,
            quantity: qty,
            unitPrice: unit,
            totalPrice: unit * qty,
            vatRate: 0,
            subItems: subs.length > 0 ? subs : undefined,
          };
        });

        const createdAt = tOrder.packageCreationDate ? new Date(tOrder.packageCreationDate) : new Date();
        // Teslimat etiketi — manuel modal `:126` birebir (model bilgisi GO/STORE eklendi)
        const trdDeliveryLabel = tOrder.deliveryType === 'GO' ? 'Uber Eats Kurye (GO)' : 'Restoran Kuryesi (STORE)';

        // ─── Adres bloku — manuel modal `:128-140` birebir
        // GO modelinde adres Trendyol'da paylasilmaz (gizlilik); manuel guard
        // uyguluyor — auto da ayni guard'i uygulayacak. Ayrica `apartmentNumber`
        // / `floor` / `doorNumber` ana adrese ` - ` ile birlestirilir.
        const trdIsGoDelivery = tOrder.deliveryType === 'GO';
        let trdDeliveryAddress = '';
        let trdAddrDescription: string | undefined;
        if (tOrder.address && !trdIsGoDelivery) {
          const mainAddr = [
            tOrder.address.address1,
            tOrder.address.neighborhood,
            tOrder.address.district,
            tOrder.address.city,
          ].filter(Boolean).join(', ');
          const detailAddr = [
            tOrder.address.apartmentNumber ? `Apt: ${tOrder.address.apartmentNumber}` : '',
            tOrder.address.floor ? `Kat: ${tOrder.address.floor}` : '',
            tOrder.address.doorNumber ? `Kapı: ${tOrder.address.doorNumber}` : '',
          ].filter(Boolean).join(' ');
          trdDeliveryAddress = [mainAddr, detailAddr].filter(Boolean).join(' - ');
          trdAddrDescription = tOrder.address.addressDescription || undefined;
        }
        // Uber fisindeki "musteri adresine erisim" QR'i. GO teslimatta adres
        // paylasilmadigi icin koordinat da gelmez; o durumda QR basilmaz.
        const trdMapsUrl = trdIsGoDelivery
          ? undefined
          : buildGoogleMapsUrl(Number(tOrder.address?.latitude), Number(tOrder.address?.longitude));

        // ─── Odeme bloku — manuel modal `:73-81,196-198` birebir
        // `paymentType !== 'PAY_WITH_ON_DELIVERY'` ise online; degilse Kapida.
        // Kapida ise `onDelivery.paymentType=CASH` -> "Kapıda Nakit", aksi
        // halde "Kapıda Kart" (manuel `getPaymentLabel` davranisi).
        // ESKI BUG: `isOnlinePaid: true` HARDCODED idi -> Kapida odemeli
        // siparislerde de "ONLINE ODENDI" damgasi bastiriyordu (kasiyer
        // odeme almiyor goruluyor). Kaldirildi.
        const trdPaymentTypeRaw = tOrder.payment?.paymentType || '';
        const trdIsOnlinePaid = trdPaymentTypeRaw !== '' && trdPaymentTypeRaw !== 'PAY_WITH_ON_DELIVERY';
        const trdPaymentLabel = (() => {
          if (trdIsOnlinePaid) return 'Online Ödeme';
          const onDel = tOrder.payment?.onDelivery?.paymentType || '';
          if (onDel === 'CASH') return 'Kapıda Nakit';
          if (onDel === 'CREDIT_CARD' || onDel === 'CARD') return 'Kapıda Kart';
          return 'Kapıda Ödeme';
        })();

        // ─── Toplamlar — manuel modal `:175-180,192-194` birebir
        // ESKI BUG: auto `totalPrice`'i NET (indirim sonrasi) kabul ediyordu;
        // modal GROSS (indirim oncesi) kabul ediyor. Promosyonlu siparislerde
        // iki fiste FARKLI tutar cikiyordu. Modal'in formulu uygulandi:
        //   subtotal      = (discountTotal>0 ? rawTotal : max(itemsTotal, grandTotal))
        //   grandTotal    = (discount>0 ? rawTotal - discount : rawTotal)
        //   discountTotal = sum(promotions.totalSellerAmount)
        const trdItemsTotal = items.reduce((sum, it) => sum + (it.totalPrice || 0), 0);
        const trdRawTotal = tOrder.totalPrice || 0;
        const trdPromotionDiscount = tOrder.promotions
          ?.reduce((sum: number, p: any) => sum + (p.totalSellerAmount || 0), 0) || 0;
        const trdGrandTotal = trdPromotionDiscount > 0 ? trdRawTotal - trdPromotionDiscount : trdRawTotal;
        const trdSubtotal = trdPromotionDiscount > 0
          ? trdRawTotal
          : (trdItemsTotal > trdGrandTotal ? trdItemsTotal : trdGrandTotal);

        receiptData = {
          orderNumber: tOrder.orderNumber || `TY-${orderId}`,
          // Manuel modal `:185` — `data.shortCode` template tarafindan
          // `data.orderNumber`'dan once kullanilir (kisa orderCode bastirir).
          shortCode: tOrder.orderCode || undefined,
          date: createdAt.toLocaleDateString('tr-TR', { timeZone: 'Europe/Istanbul' }),
          time: createdAt.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Istanbul' }),
          customerName: tOrder.customer?.firstName && tOrder.customer?.lastName ? `${tOrder.customer.firstName} ${tOrder.customer.lastName}` : 'Uber Eats Müşteri',
          customerPhone: tOrder.address?.phone || tOrder.callCenterPhone || undefined,
          customerAddress: trdDeliveryAddress || undefined,
          addressDescription: trdAddrDescription,
          items,
          subtotal: trdSubtotal,
          vatTotal: 0,
          grandTotal: trdGrandTotal,
          discountTotal: trdPromotionDiscount,
          // Manuel modal `:196-198` ile birebir — online ise "ÖDENDİ" damgasi
          paymentStatus: trdIsOnlinePaid ? 'PAID' : undefined,
          paymentMethod: trdPaymentLabel,
          isOnlinePaid: trdIsOnlinePaid,
          receiptLabel: resolveLabel('UBER EATS PAKET SIPARIS', 'trendyol_yemek', tOrder),
          deliveryType: trdDeliveryLabel,
          trendyolOrderCode: tOrder.orderNumber || tOrder.orderCode || undefined,
          callCenterPhone: tOrder.callCenterPhone || undefined,
          // Fisin orta bolumu Uber'in kendi duzeninde bassin.
          isUberEatsReceipt: true,
          supportCode: tOrder.supplierId ? String(tOrder.supplierId) : undefined,
          qrCodeRawUrl: trdMapsUrl,
          qrCodeLabel: trdMapsUrl ? 'ADRES QR' : undefined,
          // `isPreOrder` ESKI BUG: `storePickupSelected` (Gel Al) ile karistiriliyordu;
          // Trendyol'da pre-order kavrami yok, manuel modal hic set etmiyor — kaldirildi.
          // Auto'nun isleyen davranisi olan `orderNote` korunur; template `customerNote`'u
          // render etmediginden o alan kaldirildi (gereksiz alan).
          orderNote: tOrder.customerNote || undefined,
          ...resolveHeader('trendyol_yemek', tOrder),
        };

      } else {
        // ═══════════════════════════════════════════════════════════════════
        // QR/Kiosk/E-ticaret kontrolü
        // ─────────────────────────────────────────────────────────────────────
        // Bu blok platform dallarından (Getir/YS/Migros/Trendyol) SONRA çalışır;
        // platform siparişleri zaten yukarıdaki if/else if zincirinde yakalanmıştır,
        // buraya asla düşmezler. Yani platform akışları HİÇ etkilenmez.
        // Mutfak handler'ındaki QR dalına paralel; orada doğru çalıştığı kanıtlanmış.
        // ═══════════════════════════════════════════════════════════════════
        const qrOrder = [...qrMenuOrders, ...ecommerceOrders, ...kioskOrders].find(o => o.id === orderId);
        if (qrOrder) {
          console.log('🧾 [ReceiptPrint] QR/Kiosk/E-ticaret siparişi fişi yazdırılıyor...', { orderId });
          const createdAt = qrOrder.siparisOlusturmaTarihi ? new Date(qrOrder.siparisOlusturmaTarihi) : new Date();
          const masaInfo = qrOrder.masaAdi || (qrOrder.masaNumarasi ? `Masa ${qrOrder.masaNumarasi}` : '');
          const items: PackageReceiptItem[] = (qrOrder.siparisDetaylari || []).map((item: any) => ({
            name: item.urunAdi || 'Ürün',
            quantity: item.miktar || item.adet || 1,
            unitPrice: item.birimFiyat || 0,
            totalPrice: item.toplamFiyat || ((item.birimFiyat || 0) * (item.miktar || item.adet || 1)),
            vatRate: 0,
            subItems: [],
          }));
          const subtotal = items.reduce((sum, it) => sum + it.totalPrice, 0);
          receiptData = {
            orderNumber: qrOrder.siparisId?.split('-').slice(-2).join('-') || qrOrder.siparisId || `QR-${orderId}`,
            shortCode: qrOrder.siparisId || undefined,
            date: createdAt.toLocaleDateString('tr-TR', { timeZone: 'Europe/Istanbul' }),
            time: createdAt.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Istanbul' }),
            customerName: qrOrder.musteriAdi || qrOrder.kullaniciAdi || 'Müşteri',
            customerPhone: qrOrder.musteriTelefonu || qrOrder.telefon || undefined,
            customerAddress: masaInfo || undefined,
            items,
            subtotal,
            vatTotal: 0,
            grandTotal: qrOrder.toplamTutar || subtotal,
            discountTotal: 0,
            paymentMethod: qrOrder.odemeYontemi || undefined,
            orderNote: qrOrder.siparisNotu || undefined,
            // QR/kiosk/e-ticaret siparişinin markası yok; çözümleyici ekran
            // varsayılanını döndürür, davranış bugünküyle aynı kalır.
            ...resolveHeader(''),
          };
        } else {
          // ───────────────────────────────────────────────────────────────────
          // Telefon paket siparişi fallback'i (MEVCUT KOD, dokunulmadı)
          // ───────────────────────────────────────────────────────────────────
          const response = await getPackageOrderById(orderId);
          if (!response.Success || !response.Data) {
            failPrint(`Sipariş fişi: Sipariş detayı alınamadı (${response.Message || 'Bilinmeyen hata'})`);
            return;
          }
          const orderData = response.Data;
          console.log('🧾 [ReceiptPrint] Sipariş detayı alındı:', orderData);
          const createdAt = orderData.CreatedAt ? new Date(orderData.CreatedAt) : new Date();
          const receiptItems: PackageReceiptItem[] = (orderData.Items || []).map((item: any) => ({
            name: item.ProductName || 'Ürün',
            quantity: item.Quantity || 1,
            unitPrice: item.UnitPrice || 0,
            totalPrice: item.TotalAmount || item.SubTotal || 0,
            vatRate: item.TaxRate || 0,
            subItems: [],
          }));
          receiptData = {
            orderNumber: orderData.OrderNumber || `PS-${orderId}`,
            shortCode: orderData.ShortCode || undefined,
            date: createdAt.toLocaleDateString('tr-TR', { timeZone: 'Europe/Istanbul' }),
            time: createdAt.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Istanbul' }),
            customerName: orderData.AccountName || 'Müşteri',
            customerPhone: orderData.CustomerPhone || undefined,
            customerAddress: orderData.CustomerAddress || undefined,
            // Backend AccountShippingAddress join'inden gelen detayli adres
            // alanlari. Telefon paket fislerinde mahalle/cadde/sokak/apartman
            // vb. satirlari fise basabilmek icin. Local service tarafi bu
            // alanlari tek tek satir olarak yazdirir; bos olanlar gizlenir.
            mahalle: orderData.Mahalle || undefined,
            cadde: orderData.Cadde || undefined,
            sokak: orderData.Sokak || undefined,
            apartman: orderData.Apartman || undefined,
            siteAdi: orderData.SiteAdi || undefined,
            blok: orderData.Blok || undefined,
            binaNo: orderData.BinaNo || undefined,
            kat: orderData.Kat || undefined,
            daireNo: orderData.DaireNo || undefined,
            items: receiptItems,
            subtotal: orderData.SubTotal || 0,
            vatTotal: orderData.TaxAmount || 0,
            grandTotal: orderData.TotalAmount || 0,
            discountTotal: orderData.DiscountAmount || 0,
            paymentStatus: orderData.PaymentStatus || undefined,
            // Backend ham değerleri (`cash`, `CASH`, `pos`, `POS`, `credit_card`,
            // `pavopay`, `ONLINE`, ...) Türkçeye çevriliyor. Bilinmeyen değerler
            // aynen geçer — kullanıcı serbest etiket girmiş olabilir.
            paymentMethod: translatePackagePaymentMethod(orderData.PaymentMethod),
            orderNote: orderData.Notes || orderData.CustomerNotes || undefined,
            // Telefon siparişinin markası yok; ekran varsayılanı kullanılır.
            ...resolveHeader(''),
          };
        }
      }

      if (!receiptData) {
        failPrint('Sipariş fişi: Fiş verisi oluşturulamadı (sipariş kaynağı tanınmadı)');
        return;
      }

      const localServiceUrl = screenData?.localServiceUrl || undefined;
      await printPackageReceipt(receiptData, printer, localServiceUrl);
      console.log('🧾 [ReceiptPrint] Fiş yazdırma tamamlandı');
      
    } catch (error) {
      console.error('🧾 [ReceiptPrint] Hata:', error);
      failPrint(`Sipariş fişi yazdırılamadı: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`);
    }
  }, [companyId, screenData?.printingSettings, screenData?.platformIntegrations, screenData?.localServiceUrl, filteredOrders, trendyolOrders, qrMenuOrders, ecommerceOrders, kioskOrders, failPrint]);

  return { handleKitchenPrint, handleReceiptPrint };
}
